import React, { useState, useEffect } from 'react';
import {
  Clock,
  Plus,
  Edit,
  Trash2,
  Calendar,
  MoreVertical,
  Sun,
  Moon,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { officeHoursAPI, holidaysAPI } from '../services/api';

const OfficeHours = () => {
  const [officeHours, setOfficeHours] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  const [isAddHolidayOpen, setIsAddHolidayOpen] = useState(false);
  const [isEditScheduleOpen, setIsEditScheduleOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '00:00', end: '00:00' },
    sunday: { enabled: false, start: '00:00', end: '00:00' }
  });
  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: '',
    recurring: false
  });

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [hoursRes, holidaysRes] = await Promise.all([
        officeHoursAPI.list(),
        holidaysAPI.list()
      ]);
      setOfficeHours(hoursRes.data);
      setHolidays(holidaysRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSchedule = async () => {
    try {
      await officeHoursAPI.create(newSchedule);
      setIsAddScheduleOpen(false);
      setNewSchedule({
        name: '',
        monday: { enabled: true, start: '09:00', end: '17:00' },
        tuesday: { enabled: true, start: '09:00', end: '17:00' },
        wednesday: { enabled: true, start: '09:00', end: '17:00' },
        thursday: { enabled: true, start: '09:00', end: '17:00' },
        friday: { enabled: true, start: '09:00', end: '17:00' },
        saturday: { enabled: false, start: '00:00', end: '00:00' },
        sunday: { enabled: false, start: '00:00', end: '00:00' }
      });
      fetchData();
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  const handleEditSchedule = async () => {
    try {
      await officeHoursAPI.update(selectedSchedule.id, selectedSchedule);
      setIsEditScheduleOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      console.error('Error updating schedule:', error);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await officeHoursAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const handleAddHoliday = async () => {
    try {
      await holidaysAPI.create(newHoliday);
      setIsAddHolidayOpen(false);
      setNewHoliday({
        name: '',
        date: '',
        recurring: false
      });
      fetchData();
    } catch (error) {
      console.error('Error creating holiday:', error);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await holidaysAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting holiday:', error);
    }
  };

  const updateDaySchedule = (day, field, value, isNew = true) => {
    if (isNew) {
      setNewSchedule({
        ...newSchedule,
        [day]: { ...newSchedule[day], [field]: value }
      });
    } else {
      setSelectedSchedule({
        ...selectedSchedule,
        [day]: { ...selectedSchedule[day], [field]: value }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Office Hours</h1>
        <p className="text-gray-400 mt-1">Configure business hours and holiday schedules</p>
      </div>

      <Tabs defaultValue="schedules" className="w-full">
        <TabsList className="bg-[#1e1e2d] border border-[#2a2a3d]">
          <TabsTrigger value="schedules" className="data-[state=active]:bg-blue-600">
            <Clock className="w-4 h-4 mr-2" />
            Schedules
          </TabsTrigger>
          <TabsTrigger value="holidays" className="data-[state=active]:bg-blue-600">
            <Calendar className="w-4 h-4 mr-2" />
            Holidays
          </TabsTrigger>
        </TabsList>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setIsAddScheduleOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Schedule
            </Button>
          </div>

          <div className="grid gap-4">
            {officeHours.map((schedule) => (
              <Card key={schedule.id} className="bg-[#12121a] border-[#1e1e2d]">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      {schedule.name}
                    </CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSchedule(schedule);
                            setIsEditScheduleOpen(true);
                          }}
                          className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2">
                    {days.map((day, index) => (
                      <div
                        key={day}
                        className={`p-3 rounded-lg text-center ${
                          schedule[day]?.enabled
                            ? 'bg-green-500/10 border border-green-500/30'
                            : 'bg-[#1e1e2d] border border-[#2a2a3d]'
                        }`}
                      >
                        <p className={`text-xs font-medium mb-1 ${
                          schedule[day]?.enabled ? 'text-green-400' : 'text-gray-500'
                        }`}>
                          {dayLabels[index].slice(0, 3)}
                        </p>
                        {schedule[day]?.enabled ? (
                          <div className="flex items-center justify-center gap-1">
                            <Sun className="w-3 h-3 text-yellow-400" />
                            <span className="text-xs text-white">
                              {schedule[day].start}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <Moon className="w-3 h-3 text-gray-500" />
                          </div>
                        )}
                        {schedule[day]?.enabled && (
                          <p className="text-xs text-gray-400 mt-1">
                            to {schedule[day].end}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setIsAddHolidayOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Holiday
            </Button>
          </div>

          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardContent className="p-0">
              <div className="divide-y divide-[#1e1e2d]">
                {holidays.map((holiday) => (
                  <div key={holiday.id} className="p-4 flex items-center justify-between hover:bg-[#1e1e2d]/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        <Calendar className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{holiday.name}</p>
                        <p className="text-sm text-gray-400">{holiday.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {holiday.recurring && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          Recurring
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {holidays.length === 0 && (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">No holidays configured</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Schedule Modal */}
      <Dialog open={isAddScheduleOpen} onOpenChange={setIsAddScheduleOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-400">Schedule Name</Label>
              <Input
                value={newSchedule.name}
                onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                placeholder="Business Hours"
                className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-gray-400">Weekly Schedule</Label>
              {days.map((day, index) => (
                <div key={day} className="flex items-center gap-4 p-3 bg-[#1e1e2d] rounded-lg">
                  <div className="w-24">
                    <span className="text-white font-medium">{dayLabels[index]}</span>
                  </div>
                  <Switch
                    checked={newSchedule[day].enabled}
                    onCheckedChange={(checked) => updateDaySchedule(day, 'enabled', checked, true)}
                  />
                  {newSchedule[day].enabled && (
                    <>
                      <Input
                        type="time"
                        value={newSchedule[day].start}
                        onChange={(e) => updateDaySchedule(day, 'start', e.target.value, true)}
                        className="w-32 bg-[#12121a] border-[#2a2a3d] text-white"
                      />
                      <span className="text-gray-400">to</span>
                      <Input
                        type="time"
                        value={newSchedule[day].end}
                        onChange={(e) => updateDaySchedule(day, 'end', e.target.value, true)}
                        className="w-32 bg-[#12121a] border-[#2a2a3d] text-white"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddScheduleOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddSchedule} className="bg-blue-600 hover:bg-blue-700">
              Add Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Modal */}
      <Dialog open={isEditScheduleOpen} onOpenChange={setIsEditScheduleOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Schedule</DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Schedule Name</Label>
                <Input
                  value={selectedSchedule.name}
                  onChange={(e) => setSelectedSchedule({ ...selectedSchedule, name: e.target.value })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-gray-400">Weekly Schedule</Label>
                {days.map((day, index) => (
                  <div key={day} className="flex items-center gap-4 p-3 bg-[#1e1e2d] rounded-lg">
                    <div className="w-24">
                      <span className="text-white font-medium">{dayLabels[index]}</span>
                    </div>
                    <Switch
                      checked={selectedSchedule[day]?.enabled}
                      onCheckedChange={(checked) => updateDaySchedule(day, 'enabled', checked, false)}
                    />
                    {selectedSchedule[day]?.enabled && (
                      <>
                        <Input
                          type="time"
                          value={selectedSchedule[day].start}
                          onChange={(e) => updateDaySchedule(day, 'start', e.target.value, false)}
                          className="w-32 bg-[#12121a] border-[#2a2a3d] text-white"
                        />
                        <span className="text-gray-400">to</span>
                        <Input
                          type="time"
                          value={selectedSchedule[day].end}
                          onChange={(e) => updateDaySchedule(day, 'end', e.target.value, false)}
                          className="w-32 bg-[#12121a] border-[#2a2a3d] text-white"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditScheduleOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditSchedule} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Holiday Modal */}
      <Dialog open={isAddHolidayOpen} onOpenChange={setIsAddHolidayOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-400">Holiday Name</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                placeholder="Christmas Day"
                className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400">Date</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Recurring Yearly</Label>
              <Switch
                checked={newHoliday.recurring}
                onCheckedChange={(checked) => setNewHoliday({ ...newHoliday, recurring: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddHolidayOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddHoliday} className="bg-blue-600 hover:bg-blue-700">
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficeHours;
